import React, { useEffect } from "react";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { useDisclosure } from "@chakra-ui/hooks";
import {
  createColumnHelper, flexRender, getCoreRowModel,
  getExpandedRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import { getBlackListHistoryById } from "api/blackListClient";
import DivLoader from "components/divloader/DivLoader";
import { toast } from "react-hot-toast";
import { MdHighlightOff, MdHistory } from "react-icons/md";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import InfoModal from "./InfoModal";
import moment from "moment";
import DatePicker from "react-datepicker";

const columnHelper = createColumnHelper<any>();

const ViewHistoryModal = ({
  fetchBlackListClientsData, id, info, roleData, infoData, tableData,
}: {
  fetchBlackListClientsData: () => void;
  id?: string; info?: boolean; roleData?: any; infoData?: any; tableData?: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [filterFormData, setFilterFormData] = React.useState<any>({});
  const [data, setData] = React.useState([]);
  const [allData, setAllData] = React.useState([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const handleClose = () => {
    setData([]); setFilterFormData({});
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  async function fetchHistory(id: string) {
    setIsLoading(true);
    await getBlackListHistoryById(id)
      .then((res: any) => { setData(res); setAllData(res); })
      .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch history"))
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (Array.isArray(allData) && filterFormData?.starting_date && filterFormData?.ending_date) {
      setData(allData.filter((f: any) => {
        const d = moment(f?.created_at).format("YYYY-MM-DD");
        return moment(filterFormData.starting_date).format("YYYY-MM-DD") <= d &&
               moment(filterFormData.ending_date).format("YYYY-MM-DD") >= d;
      }));
    } else {
      setData(Array.isArray(allData) ? allData : []);
    }
  }, [filterFormData?.ending_date]);

  const columns = [
    columnHelper.accessor("client_name", {
      id: "client_name",
      header: () => <span className="blh-th">Client Name</span>,
      cell: (info) => (
        <div className="blh-client-cell">
          <div className="blh-client-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "?"}</div>
          <span className="blh-cell-text">{info.getValue() || "—"}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.client_name || "";
        const b = rowB.original.client_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
      enableSorting: true,
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="blh-th">Date & Time</span>,
      cell: (info) => (
        <div className="blh-date-cell">
          <div className="blh-date-icon">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span className="blh-cell-text">{info.getValue()}</span>
        </div>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor("request", {
      id: "request",
      header: () => <span className="blh-th">Request</span>,
      cell: (info) => (
        <InfoModal heading={info.row.original?.client_name} info={info.getValue()} />
      ),
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  if (!info || !roleData?.[0]?.blacklist?.value?.view_blacklist) return null;

  return (
    <>
      <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
        <PopoverTrigger>
          <button className="blh-trigger-btn" onClick={() => { if (id) fetchHistory(id); onOpen(); }} aria-label="View history" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
            <MdHistory size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="160px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View History</PopoverHeader>
          <PopoverArrow bg="#eff6ff" />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`blh-panel${isOpen && !isCloseModal ? " blh-panel--open" : " blh-panel--close"}`}>
          <div className="blh-glow" />

          <div className="blh-header">
            <div className="blh-header-brand">
              <div className="blh-header-logo">
                <MdHistory size={19} color="#fff" />
              </div>
              <div>
                <h2 className="blh-header-title">Blacklist History</h2>
                <p className="blh-header-sub">
                  {infoData?.row?.original?.first_name
                    ? `${infoData.row.original.first_name} ${infoData.row.original.last_name || ""}`.trim()
                    : "Activity log"}
                </p>
              </div>
            </div>
            <button className="blh-close" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          <div className="blh-body">
            {/* Date filter */}
            <div className="blh-filter-section">
              {/* <div className="blh-filter-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div> */}
              <div className="blh-filter-content">
                <label className="blh-filter-label">Date Range Filter</label>
                <div className="blh-datepicker-wrap">
                  <DatePicker
                    onKeyDown={(e) => e.preventDefault()}
                    className="blh-datepicker"
                    placeholderText="Select date range…"
                    startDate={filterFormData?.starting_date ? new Date(filterFormData.starting_date) : null}
                    endDate={filterFormData?.ending_date ? new Date(filterFormData.ending_date) : null}
                    maxDate={new Date()}
                    selectsRange={true}
                    isClearable={true}
                    onChange={(dateRange: any) => {
                      const [a, b] = dateRange;
                      setFilterFormData((p: any) => ({ ...p, starting_date: a, ending_date: b }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="blh-stats">
              <div className="blh-stat">
                <span className="blh-stat-label">Active Range</span>
                <span className="blh-stat-value">
                  {filterFormData?.starting_date && filterFormData?.ending_date
                    ? `${moment(filterFormData.starting_date).format("YYYY-MM-DD")} → ${moment(filterFormData.ending_date).format("YYYY-MM-DD")}`
                    : "All time"}
                </span>
              </div>
              <div className="blh-stat">
                <span className="blh-stat-label">Total Events</span>
                <span className="blh-stat-value blh-stat-value--hl">{data?.length ?? 0}</span>
              </div>
            </div>

            {/* Table */}
            <div className="blh-table-wrap">
              {isLoading ? (
                <div className="blh-loading"><div className="blh-spinner" /><p>Loading history…</p></div>
              ) : (
                <table className="blh-table">
                  <thead>
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => (
                          <th
                            key={h.id} colSpan={h.colSpan}
                            onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                            className={`blh-thead-th${h.column.getCanSort() ? " blh-thead-th--sort" : ""}`}
                          >
                            <div className="blh-th-inner">
                              {flexRender(h.column.columnDef.header, h.getContext())}
                              {h.column.getCanSort() && (
                                <span className="blh-sort-icon">
                                  {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                                   h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                                   <RiArrowUpDownFill size={11} />}
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row, ri) => (
                        <tr key={row.id} className={`blh-tbody-tr${ri % 2 !== 0 ? " blh-tbody-tr--alt" : ""}`}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="blh-tbody-td">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3}>
                        <div className="blh-empty">
                          <div className="blh-empty-icon"><MdHistory size={22} /></div>
                          <p className="blh-empty-title">No history found</p>
                          <p className="blh-empty-sub">Try adjusting the date filter</p>
                        </div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="blh-footer">
            <span className="blh-footer-hint">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Showing blacklist trigger events
            </span>
            <button className="blh-btn-close" onClick={handleClose}>Close</button>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .blh-trigger-btn { width:30px; height:30px; border-radius:8px; background:#eff6ff; border:1.5px solid #bfdbfe; color:#2563eb; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
        .blh-trigger-btn:hover { background:#dbeafe; border-color:#93c5fd; }

        .blh-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:660px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1);  }
        .blh-panel--open  { transform:translateX(0); }
        .blh-panel--close { transform:translateX(100%); }

        .blh-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#881337,#e11d48,#f43f5e,#e11d48,#881337); background-size:200% 100%; animation:blhGlow 3s linear infinite; }
        @keyframes blhGlow { 0%{background-position:0%} 100%{background-position:200%} }
 
        .blh-header { padding:15px 26px; background:linear-gradient(135deg,#881337 0%,#be123c 60%,#e11d48 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .blh-header-brand { display:flex; align-items:center; gap:13px; }
        .blh-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .blh-header-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .blh-header-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .blh-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .blh-close:hover { color:#fff; }

        .blh-body { flex:1; overflow-y:auto; overflow-x:hidden; padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
        .blh-body::-webkit-scrollbar { width:4px; }
        .blh-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        .blh-filter-section { display:flex; align-items:flex-start; gap:12px; }
        .blh-filter-icon { width:36px; height:36px; border-radius:9px; flex-shrink:0; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; color:#e11d48; margin-top:2px; }
        .blh-filter-content { flex:1; display:flex; flex-direction:column; gap:5px; }
        .blh-filter-label { font-size:12.5px; font-weight:700; color:#374151; }
        .blh-datepicker-wrap .react-datepicker-wrapper { width:100%; }
        .blh-datepicker { width:100%; padding:10px 13px; background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:10px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; color:#0f172a; outline:none; transition:border-color .18s; box-sizing:border-box; }
        .blh-datepicker:focus { border-color:#e11d48; box-shadow:0 0 0 3px rgba(225,29,72,.08); }

        .blh-stats { display:flex; background:#f8fafc; border:1px solid #e2e8f0; border-radius:11px;  }
        .blh-stat { flex:1; padding:11px 15px; display:flex; flex-direction:column; gap:3px; border-right:1px solid #e2e8f0; }
        .blh-stat:last-child { border-right:none; }
        .blh-stat-label { font-size:10.5px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.5px; }
        .blh-stat-value { font-size:13px; font-weight:700; color:#0f172a; }
        .blh-stat-value--hl { color:#e11d48; }

        .blh-table-wrap { overflow-x:auto; border:1px solid #e2e8f0; border-radius:12px;  }
        .blh-table-wrap::-webkit-scrollbar { display: none; }
        .blh-table { width:100%; border-collapse:collapse; }
        .blh-thead-th { padding:10px 14px 8px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; }
        .blh-thead-th--sort { cursor:pointer; }
        .blh-thead-th--sort:hover { background:#f1f5f9; }
        .blh-th-inner { display:flex; align-items:center; gap:5px; }
        .blh-th { font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .blh-sort-icon { color:#94a3b8; display:flex; align-items:center; }
        .blh-tbody-tr { transition:background .12s; }
        .blh-tbody-tr:hover { background:#fff7f7; }
        .blh-tbody-tr--alt { background:#fafbfc; }
        .blh-tbody-td { padding:11px 14px; border-bottom:1px solid #f1f5f9; }

        .blh-client-cell { display:flex; align-items:center; gap:8px; }
        .blh-client-avatar { width:26px; height:26px; border-radius:7px; flex-shrink:0; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; color:#e11d48; }
        .blh-date-cell { display:flex; align-items:center; gap:7px; }
        .blh-date-icon { width:22px; height:22px; border-radius:6px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#94a3b8; flex-shrink:0; }
        .blh-cell-text { font-size:12.5px; font-weight:600; color:#0f172a; }

        .blh-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:32px; gap:8px; }
        .blh-empty-icon { width:44px; height:44px; border-radius:11px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .blh-empty-title { font-size:13px; font-weight:700; color:#64748b; }
        .blh-empty-sub { font-size:12px; color:#94a3b8; }

        .blh-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px; gap:10px; font-size:13px; color:#94a3b8; }
        .blh-spinner { width:24px; height:24px; border:2.5px solid #e2e8f0; border-top-color:#e11d48; border-radius:50%; animation:blhSpin .7s linear infinite; }
        @keyframes blhSpin { to { transform:rotate(360deg); } }

        .blh-footer { padding:12px 24px; border-top:1px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .blh-footer-hint { display:flex; align-items:center; gap:5px; font-size:12px; color:#94a3b8; font-weight:500; }
        .blh-btn-close { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:8px 16px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .blh-btn-close:hover { background:#f1f5f9; }

        @media (max-width:640px) { .blh-panel { max-width:100%; } }
      `}</style>
    </>
  );
};

export default ViewHistoryModal;